// istanbul ignore file
import { useNavigate } from 'react-router-dom';
import { lazy, useCallback, useEffect, useMemo } from 'react';

import { ServerError } from '@cld/empty-state';
import { useRemoteState } from '@cld/use-promise-state';
import { RemoteModule } from '../components/remote-module/remote-module';
import { modules } from '../components/side-menu/side-menu.constants';
import { Loader } from '../components/loader';
import { useMessage } from '../i18n/useMessage';
import { ApiClient } from '../api/createApiClient';
import { useQuerySeed } from '../hooks/util.hooks';
import { UnicornApi } from '@cld/unicorn-api';

const defaultEventCategory = 'developer dashboard';

const RemoteDeveloperDashboard = lazy(() =>
  import('@cld-remote/developer_dashboard/index').then(({ DeveloperDashboardPage }) => ({
    default: DeveloperDashboardPage,
  }))
);

export interface DeveloperDashboardProps {
  apiClient: ApiClient;
  navigateOutsideApplication(path: string): void;
  usedNewDashboardBeforeFullRelease: boolean;
  unicornApi: UnicornApi;
}

export const DeveloperDashboard = ({ apiClient, navigateOutsideApplication, usedNewDashboardBeforeFullRelease, unicornApi }: DeveloperDashboardProps) => {
  const {
    account,
    productEnvironment,
    deprecatedData: {
      sessionNotifications: { billingNotification, disabledEntity, emailNotification, invoiceNotification },
    },
  } = unicornApi.data;

  const t = useMessage();
  const navigate = useNavigate();
  const seed = useQuerySeed();
  const fetchClient = useMemo(() => unicornApi.methods.createFetchClient(), [unicornApi.methods.createFetchClient]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<{ mediaLibraryNav?: unknown }>) => {
      if (!event.origin.endsWith('.cloudinary.com')) {
        return;
      }

      if (event.data.mediaLibraryNav) {
        navigateOutsideApplication(`/console/c-${productEnvironment.externalId}/media_library`);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [productEnvironment, navigateOutsideApplication]);

  const remoteDataCallback = useCallback(
    () =>
      apiClient.fetchCustomersData(productEnvironment.externalId).catch((error) => {
        console.error(error);
        return Promise.reject(error);
      }),
    [apiClient, productEnvironment.externalId]
  );
  const { state: remoteDataState } = useRemoteState(remoteDataCallback);

  const navigateToUpgradeOptions = useCallback(() => {
    if (account.newBillingSystem) {
      navigateOutsideApplication(`/settings/c-${productEnvironment.externalId}/billing/change_plan/programmable_media`);
    } else {
      navigate(`../${modules.upgradeOptions}`);
    }
  }, [productEnvironment, navigate, navigateOutsideApplication, remoteDataState]);

  const navigateToErrorReports = useCallback(() => {
    navigate(`../${modules.errorReports}`);
  }, [navigate]);

  const navigateToOldDashboard = useCallback(() => {
    navigate(`../${modules.usageReports}`);
  }, [navigate]);

  return (
    <RemoteModule documentTitle={t('developer-dashboard')}>
      {remoteDataState.status === 'pending' && <Loader />}
      {remoteDataState.status === 'error' && <ServerError />}
      {remoteDataState.status === 'idle' && (
        <RemoteDeveloperDashboard
          // TODO: remove token prop once new version of dev dashboard is deployed to prod
          // @ts-ignore
          token={unicornApi.data.deprecatedData.jwt}
          fetchClient={fetchClient}
          unicornApi={unicornApi}
          seed={seed}
          onTrack={({ eventAction, eventCategory = defaultEventCategory, eventLabel }) =>
            unicornApi.methods.sendAnalytics({
              variant: 'universal',
              data: { action: eventAction, category: eventCategory, label: eventLabel },
            })
          }
          accountId={account.id}
          customerId={productEnvironment.id}
          customersCount={remoteDataState.data.meta.total_entries}
          sessionNotifications={{
            billing_notification: billingNotification
              ? {
                  reason: billingNotification.reason,
                  data: { billing_settings_path: billingNotification.data.billingSettingsPath },
                }
              : null,
            disabled_entity: disabledEntity ? { reason: disabledEntity.reason } : null,
            email_notification: emailNotification ? { reason: emailNotification.reason } : null,
            invoice_notification: invoiceNotification
              ? {
                  reason: invoiceNotification.reason,
                  data: invoiceNotification.data.requestPaymentUpdate
                    ? {
                        payment_settings_link: invoiceNotification.data.paymentSettingsLink,
                        request_payment_update: invoiceNotification.data.requestPaymentUpdate,
                      }
                    : { request_payment_update: invoiceNotification.data.requestPaymentUpdate },
                }
              : null,
          }}
          navigateToOldDashboard={navigateToOldDashboard}
          showCurrentPlanUsageCard={usedNewDashboardBeforeFullRelease}
          showOldDashboardBanner={!usedNewDashboardBeforeFullRelease}
          navigateToUpgradePlan={navigateToUpgradeOptions}
          navigateToErrorReporting={navigateToErrorReports}
          cloudName={productEnvironment.cloudName}
          customerExternalId={productEnvironment.externalId}
          isNewBilling={account.newBillingSystem}
        />
      )}
    </RemoteModule>
  );
};
